"use client";

/**
 * Imperative store for the global post-purchase confirmation modal.
 *
 * Mirrors the ergonomics of sonner's `toast()` — any client component that
 * detects a successful purchase calls `showPurchaseConfirmation(input)` and the
 * root-mounted <PurchaseConfirmationHost /> renders the modal. Backed by a
 * module-level store + `useSyncExternalStore` (not a window event) so we can
 * pass rich, non-serializable data through and keep a single active modal.
 */
import { useSyncExternalStore } from "react";
import type { PurchaseConfirmationInput } from "@/lib/billing/purchase-confirmation";

/**
 * A queued confirmation plus a monotonic id so re-shows re-mount the modal. An
 * intersection (not `interface extends`) because the input is a discriminated
 * union — intersecting distributes over it and keeps the `kind` discriminant.
 */
export type PurchaseConfirmationRequest = PurchaseConfirmationInput & {
  id: number;
};

let current: PurchaseConfirmationRequest | null = null;
let nextId = 1;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

/**
 * Show the post-purchase confirmation modal. Only ever call this from a
 * confirmed-success path (a Stripe success-url return or an in-app purchase
 * that resolved OK) — never on cancellation or failure.
 */
export function showPurchaseConfirmation(input: PurchaseConfirmationInput) {
  current = { ...input, id: nextId++ };
  emit();
}

/** Dismiss the active confirmation (CTA click, close button, or backdrop). */
export function dismissPurchaseConfirmation() {
  if (current === null) return;
  current = null;
  emit();
}

// ── Cross-navigation hand-off ────────────────────────────────────────────────
// Some purchases complete WITHOUT a Stripe redirect — e.g. an in-app upgrade
// charged against a saved card — and then do a full-page reload to refresh
// credits. A module store can't survive that reload, so the confirmation is
// stashed in sessionStorage and replayed by the root host on the next mount.
// sessionStorage (not a URL param) keeps it off the address bar and, because it
// is consumed once, a manual refresh never re-shows it.
const HANDOFF_KEY = "panda:pending-purchase-confirmation";

/** Queue a confirmation to show after the next full-page navigation/reload. */
export function queuePurchaseConfirmation(input: PurchaseConfirmationInput) {
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(input));
  } catch {
    /* sessionStorage unavailable (private mode / SSR) — non-fatal */
  }
}

/** Read and clear a queued confirmation. Called once by the root host on mount. */
export function consumeQueuedPurchaseConfirmation(): PurchaseConfirmationInput | null {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(HANDOFF_KEY);
    return JSON.parse(raw) as PurchaseConfirmationInput;
  } catch {
    return null;
  }
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot(): PurchaseConfirmationRequest | null {
  return current;
}

function getServerSnapshot(): PurchaseConfirmationRequest | null {
  return null;
}

/** Subscribe a component to the active confirmation (null when none). */
export function usePurchaseConfirmation(): PurchaseConfirmationRequest | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
